import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { AutoEnrollRule } from './autoEnrollTypes'
import { getAutoEnrollRules, saveAutoEnrollRules } from './autoEnrollActions'

const TEMP_ID_PREFIX = 'temp-'

export const isTempId = (id: string) => id.startsWith(TEMP_ID_PREFIX)

const generateTempId = () =>
  `${TEMP_ID_PREFIX}${Date.now()}-${Math.random().toString(36).slice(2, 9)}`

export interface AutoEnrollState {
  rules: AutoEnrollRule[]
  /** Ids of rules last loaded from server; used on Save to compute deletes. */
  savedRuleIds: string[]
  rulesLoaded: boolean
  saveLoading: boolean
}

export const initialState: AutoEnrollState = {
  rules: [],
  savedRuleIds: [],
  rulesLoaded: false,
  saveLoading: false,
}

const autoEnrollSlice = createSlice({
  name: 'autoEnroll',
  initialState,
  reducers: {
    getAutoEnrollRulesSuccess(state, action: PayloadAction<AutoEnrollRule[]>) {
      const rules = (action.payload ?? []).map((r) => ({
        ...r,
        segments: Array.isArray(r.segments) ? r.segments : [],
        tags: Array.isArray(r.tags) ? r.tags : [],
      }))
      state.rules = rules
      state.savedRuleIds = rules.map((r) => r.id)
      state.rulesLoaded = true
    },
    getAutoEnrollRulesFailed(state) {
      state.rulesLoaded = true
    },
    /** Local only: add a new rule (temp id). Persisted on Save Settings. */
    addAutoEnrollRule(
      state,
      action: PayloadAction<Omit<AutoEnrollRule, 'id'>>
    ) {
      state.rules.push({
        ...action.payload,
        id: generateTempId(),
      })
    },
    /** Local only: remove a rule. Persisted on Save Settings. */
    removeAutoEnrollRule(state, action: PayloadAction<string>) {
      state.rules = state.rules.filter((r) => r.id !== action.payload)
    },
    /** Local only: update a rule. Persisted on Save Settings. */
    updateAutoEnrollRule(
      state,
      action: PayloadAction<{
        id: string
        changes: Partial<Omit<AutoEnrollRule, 'id'>>
      }>
    ) {
      state.rules = state.rules.map((rule) =>
        rule.id === action.payload.id
          ? { ...rule, ...action.payload.changes }
          : rule
      )
    },
    setSaveLoading(state, action: PayloadAction<boolean>) {
      state.saveLoading = action.payload
    },
    saveAutoEnrollRulesSuccess(state) {
      state.saveLoading = false
    },
    saveAutoEnrollRulesFailed(state) {
      state.saveLoading = false
    },
  },
})

export const autoEnrollReducer = autoEnrollSlice.reducer
export const autoEnrollActions = {
  ...autoEnrollSlice.actions,
  getAutoEnrollRules,
  saveAutoEnrollRules,
}
